import React, { useMemo, useState } from "react";
import {
  ArrowUpDown,
  ChevronDown,
  Download,
  Filter,
  RotateCcw,
  Search,
} from "lucide-react";
import { ArrowLeft, Calender } from "../AdminIcons";
import FormSelect from "../common/FormSelect";
import PaymentDetailsModal from "./PaymentDetailsModal";
import { useGetAllEnrollmentsQuery } from "../../services/enrollment/enrollmentApi";
import { useGetAllCategoriesQuery } from "../../services/categories/categoriesApi";

const PERIOD_OPTIONS = ["Last 7 Days", "Last 30 Days", "Last 90 Days"];
const DEFAULT_CATEGORY_OPTION = "All Categories";
const TYPE_OPTIONS = ["Type", "Live Classes", "Recorded Lectures"];
const PAGE_SIZE_OPTIONS = ["5", "10", "20"];

const getInitials = (fullName) => {
  const parts = fullName.split(" ").filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const DropDownBold = () => (
  <svg
    width="13"
    height="9"
    viewBox="0 0 13 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.38394 8.50991L0 2.12748L2.12898 0L6.38394 4.25495L10.6389 0L12.7679 2.12748L6.38394 8.50991Z"
      fill="black"
    />
  </svg>
);

const ManagePurchaseView = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [periodFilter, setPeriodFilter] = useState(PERIOD_OPTIONS[2]);
  const [categoryFilter, setCategoryFilter] = useState(DEFAULT_CATEGORY_OPTION);
  const [typeFilter, setTypeFilter] = useState(TYPE_OPTIONS[0]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const todayLabel = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const { data: categoriesResponse } = useGetAllCategoriesQuery({
    page: 1,
    limit: 100,
  });

  const categoryOptions = useMemo(() => {
    const categories = Array.isArray(categoriesResponse?.data)
      ? categoriesResponse.data
      : [];

    return [
      DEFAULT_CATEGORY_OPTION,
      ...categories.map((category) => category?.categoryName).filter(Boolean),
    ];
  }, [categoriesResponse]);

  const enrollmentQueryParams = useMemo(() => {
    const params = {
      page: currentPage,
      limit: pageSize,
      search: searchTerm.trim(),
    };

    if (periodFilter === "Last 7 Days" || periodFilter === "Last 30 Days") {
      params.dateRange = periodFilter;
    }

    if (categoryFilter && categoryFilter !== DEFAULT_CATEGORY_OPTION) {
      params.category = categoryFilter;
    }

    if (typeFilter && typeFilter !== "Type") {
      params.type = typeFilter;
    }

    return params;
  }, [
    currentPage,
    pageSize,
    searchTerm,
    periodFilter,
    categoryFilter,
    typeFilter,
  ]);

  const {
    data: apiResponse,
    isLoading,
    error,
  } = useGetAllEnrollmentsQuery(enrollmentQueryParams);

  const rows = useMemo(() => {
    if (!apiResponse?.data || !Array.isArray(apiResponse.data)) return [];

    return apiResponse.data.map((item, index) => {
      const createdAt = item.createdAt ? new Date(item.createdAt) : null;
      const fallbackName = [item.user?.firstName, item.user?.lastName]
        .filter(Boolean)
        .join(" ");
      const fallbackEmail = item.user?.email;
      const avatar = item.user?.profilePicture?.secureUrl || null;
      const paymentScreenshot = item.paymentScreenshot.secureUrl;
      const rawAmount = item.course.coursePrice || 0;

      return {
        id: item._id,
        orderId: item.invoiceNumber,
        date: createdAt
          ? createdAt.toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : "N/A",
        time: createdAt
          ? createdAt
              .toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
              .toLowerCase()
          : "N/A",
        course: item.course.courseTitle || "N/A",
        type: item.enrollmentType || "Course",
        studentName: fallbackName,
        studentEmail: fallbackEmail,
        avatar,
        amount: `Rs${Number(rawAmount || 0).toFixed(2)}`,
        status: item.enrollmentStatus || "Pending",
        phoneNumber: item.user.phoneNumber,
        paymentScreenshot,
      };
    });
  }, [apiResponse]);

  const pagination = useMemo(
    () => apiResponse?.pagination || {},
    [apiResponse],
  );

  const totalPages = Math.max(1, pagination.pages || 1);
  const displayedPage = pagination.currentPage || currentPage;
  const totalRecords = pagination.total ?? apiResponse?.count ?? rows.length;
  const pageStartIndex =
    totalRecords === 0 ? 0 : (displayedPage - 1) * pageSize;
  const pageRows = rows;

  const visibleIds = pageRows.map((row) => row.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleToggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setPeriodFilter(PERIOD_OPTIONS[1]);
    setCategoryFilter(DEFAULT_CATEGORY_OPTION);
    setTypeFilter("Type");
    setPageSize(10);
    setCurrentPage(1);
  };

  const handleOpenDetails = (purchase) => {
    setSelectedPurchase(purchase);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedPurchase(null);
  };

  const showFrom = totalRecords === 0 ? 0 : pageStartIndex + 1;
  const showTo = Math.min(pageStartIndex + pageRows.length, totalRecords);

  const handleExportCSV = () => {
    if (rows.length === 0) {
      return;
    }

    const headers = [
      "Order ID",
      "Order Date",
      "Order Time",
      "Course",
      "Type",
      "Student Name",
      "Student Email",
      "Amount",
      "Status",
    ];

    // Convert rows to CSV format
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        [
          `"${row.orderId}"`,
          `"${row.date}"`,
          `"${row.time}"`,
          `"${row.course}"`,
          `"${row.type}"`,
          `"${row.studentName}"`,
          `"${row.studentEmail}"`,
          `"${row.amount}"`,
          `"${row.status}"`,
        ].join(","),
      ),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `purchases_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="bg-white pl-0 sm:pl-6">
      <div className="bg-[#f5f5f5] pb-6">
        <div className="px-3 pt-6 font-['Inter'] sm:px-5">
          <p className="text-sm text-[#6B7280]">
            Dashboard &gt;{" "}
            <span className="font-medium text-[#374151]">Payments</span>{" "}
          </p>

          <div className="mt-4 flex flex-col gap-3 text-[#6B7280] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 sm:gap-5">
              {" "}
              <button type="button" aria-label="Go back">
                <ArrowLeft />
              </button>
              <h2 className="text-xl sm:text-2xl  font-bold   text-[#111827]">
                Manage Purchases
              </h2>
            </div>
            <p className="flex items-center gap-2 text-sm font-normal text-[#6B7280] sm:justify-end">
              <span>{todayLabel}</span>
              <Calender />
            </p>
          </div>

          <div className="mt-4 rounded-xl bg-white pt-6 sm:pt-8 pb-6 px-3 sm:px-6 shadow-[0_1px_2px_#0000000D]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <label className="flex h-9 w-full sm:max-w-[384px] items-center gap-2 rounded-lg bg-[#F9FAFB] px-4 text-[#6B7280]">
                <Search size={16} />
                <input
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search by ID, student, or course"
                  className="h-full w-full bg-[#F9FAFB] text-xs sm:text-sm outline-none placeholder:text-[#9CA3AF]"
                />
              </label>

              {rows.length > 0 && (
                <button
                  type="button"
                  onClick={handleExportCSV}
                  className="inline-flex h-10 w-full sm:w-auto items-center justify-center sm:justify-start gap-2 rounded bg-[#F3F4F6] px-4 text-sm sm:text-base text-[#374151]"
                >
                  <Download size={18} />
                  Export
                </button>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 font-['Roboto'] sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center lg:gap-2.5">
              <div className="inline-flex items-center gap-1 px-0 sm:gap-2 sm:px-1 lg:col-auto">
                <Filter size={20} className="text-black" />
                <span className="text-sm sm:text-xl font-['Roboto'] font-medium">
                  Filters:
                </span>
              </div>

              <FormSelect
                id="purchase-period-filter"
                options={PERIOD_OPTIONS.map((option) => ({
                  value: option,
                  label: option,
                }))}
                icon={DropDownBold}
                value={periodFilter}
                emptyByDefault={false}
                className="w-full lg:min-w-[230px]"
                controlWrapperClassName="mt-0"
                selectClassName="h-10 sm:h-[52px] rounded border border-[#D9D9D9] px-2 sm:px-3 text-sm sm:text-xl font-medium text-black"
                dropdownClassName="left-0 right-auto max-h-[45vh] min-w-full overflow-y-auto rounded border border-[#D9D9D9] bg-white"
                optionClassName="px-3 py-2 text-sm sm:text-base text-black"
                onChange={(value) => {
                  setPeriodFilter(value);
                  setCurrentPage(1);
                }}
              />

              <FormSelect
                id="purchase-category-filter"
                options={categoryOptions.map((option) => ({
                  value: option,
                  label: option,
                }))}
                icon={DropDownBold}
                value={categoryFilter}
                emptyByDefault={false}
                className="w-full lg:min-w-[200px]"
                controlWrapperClassName="mt-0"
                selectClassName="h-10 sm:h-[52px] rounded border border-[#D9D9D9] px-2 sm:px-3 text-sm sm:text-xl font-medium text-black"
                dropdownClassName="left-0 right-auto max-h-[45vh] min-w-full overflow-y-auto rounded border border-[#D9D9D9] bg-white"
                optionClassName="px-3 py-2 text-sm sm:text-base text-black"
                onChange={(value) => {
                  setCategoryFilter(value);
                  setCurrentPage(1);
                }}
              />

            

              <FormSelect
                id="purchase-type-filter"
                options={TYPE_OPTIONS.map((option) => ({
                  value: option,
                  label: option,
                }))}
                icon={DropDownBold}
                value={typeFilter}
                emptyByDefault={false}
                className="w-full lg:min-w-[140px]"
                controlWrapperClassName="mt-0"
                selectClassName="h-10 sm:h-[52px] rounded border border-[#D9D9D9] px-2 sm:px-3 text-sm sm:text-xl font-medium text-black"
                dropdownClassName="left-0 right-auto max-h-[45vh] min-w-full overflow-y-auto rounded border border-[#D9D9D9] bg-white"
                optionClassName="px-3 py-2 text-sm sm:text-base text-black"
                onChange={(value) => {
                  setTypeFilter(value);
                  setCurrentPage(1);
                }}
              />

              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex h-10 w-full items-center justify-center gap-1 rounded bg-[#923D8C] px-4 text-sm font-medium text-white sm:h-[52px] sm:gap-2 sm:px-7 sm:text-xl lg:w-auto"
              >
                <RotateCcw size={18} />
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 px-3 text-[#6B7280] sm:px-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="inline-flex gap-2">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={handleToggleSelectAllVisible}
                disabled={isLoading || pageRows.length === 0}
                className="h-5 w-5 rounded border-2 border-[#D1D5DB] accent-[#923D8C]"
              />

              <button
                type="button"
                className="inline-flex text-xs sm:text-sm items-center gap-2.5 text-[#374151]"
              >
                Bulk Actions
                <ChevronDown size={14} />
              </button>
            </div>
            <span className="text-xs sm:text-sm">
              {selectedIds.length} items selected
            </span>
          </div>

          <div className="inline-flex flex-col gap-3 text-xs text-[#6B7280] sm:flex-row sm:items-center sm:gap-4 sm:text-sm">
            <span className="sm:truncate">
              Showing{" "}
              <span className="font-medium">
                {" "}
                {showFrom}-{showTo}{" "}
              </span>{" "}
              of <span className="font-medium"> {totalRecords} </span>{" "}
              transactions
            </span>
            <div className="w-full min-w-[120px] sm:w-auto">
              <FormSelect
                id="purchase-page-size"
                options={PAGE_SIZE_OPTIONS.map((option) => ({
                  value: option,
                  label: `${option} per page`,
                }))}
                value={String(pageSize)}
                emptyByDefault={false}
                controlWrapperClassName="mt-0"
                className="min-w-[120px]"
                selectClassName="h-auto min-h-0 rounded-none border-0 bg-transparent px-0 py-0 pr-5 text-xs sm:text-sm text-[#374151] shadow-none"
                dropdownClassName="left-0 right-auto max-h-[45vh] min-w-full overflow-y-auto rounded-lg border border-[#938A96] bg-white"
                optionClassName="px-3 py-2 text-sm text-[#1F2937]"
                iconClassName="right-0 text-[#374151]"
                onChange={(value) => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 mx-3 sm:mx-6 overflow-hidden rounded-lg bg-white shadow-[0_1px_2px_#0000000D]">
          <div className="overflow-x-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-[#E5E7EB] [&::-webkit-scrollbar-thumb]:bg-[#923D8C] [&::-webkit-scrollbar-thumb]:rounded-full">
            <table className="w-full border-collapse min-w-[800px]">
              <thead className="bg-[#F9FAFB]">
                <tr className="[&>th]:px-2 sm:[&>th]:px-4 [&>th]:py-3 sm:[&>th]:py-4 [&>th]:text-left [&>th]:text-[10px] sm:[&>th]:text-xs [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-[0.6px] [&>th]:text-[#6B7280]">
                  <th className="w-10 sm:w-14">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={handleToggleSelectAllVisible}
                      disabled={isLoading || pageRows.length === 0}
                      className="h-5 w-5 rounded border-2 border-[#D1D5DB] accent-[#923D8C]"
                    />
                  </th>
                  <th className="whitespace-nowrap">ORDER ID</th>
                  <th className="whitespace-nowrap">ORDER DATE</th>
                  <th className="whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      COURSE
                      <ArrowUpDown size={12} className="hidden sm:inline" />
                    </span>
                  </th>
                  <th className="whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      TYPE
                      <ArrowUpDown size={12} className="hidden sm:inline" />
                    </span>
                  </th>
                  <th className="whitespace-nowrap hidden sm:table-cell">
                    <span className="inline-flex items-center gap-1">
                      STUDENT
                      <ArrowUpDown size={12} />
                    </span>
                  </th>
                  <th className="whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      AMOUNT
                      <ArrowUpDown size={12} className="hidden sm:inline" />
                    </span>
                  </th>
                  <th className="whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      STATUS
                      <ArrowUpDown size={12} className="hidden sm:inline" />
                    </span>
                  </th>
                  <th className="whitespace-nowrap">ACTIONS</th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 sm:px-4 py-6 sm:py-10 text-center text-sm sm:text-base text-[#6B7280]"
                    >
                      Loading purchases...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 sm:px-4 py-6 sm:py-10 text-center text-sm sm:text-base text-[#B91C1C]"
                    >
                      Failed to load purchases. Please try again.
                    </td>
                  </tr>
                ) : null}

                {!isLoading && !error
                  ? pageRows.map((row) => {
                      const selected = selectedIds.includes(row.id);

                      return (
                        <tr
                          key={row.id}
                          className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB] text-xs sm:text-sm"
                        >
                          <td className="px-2 sm:px-4 py-3 sm:py-4">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => handleToggleRow(row.id)}
                              className="h-5 w-5 rounded border-2 border-[#D1D5DB] accent-[#923D8C]"
                            />
                          </td>
                          <td
                            className="px-2 sm:px-4 py-3 sm:py-4 font-medium text-[#111827] whitespace-nowrap truncate"
                            title={
                              row.orderId?.length > 12 ? row.orderId : undefined
                            }
                          >
                            {row.orderId?.length > 12
                              ? `${row.orderId.slice(0, 12)}...`
                              : row.orderId}
                          </td>
                          <td className="px-2 sm:px-4 py-3 sm:py-4">
                            <p className="text-[#6B7280] whitespace-nowrap truncate">
                              {row.date}
                            </p>
                            <p className="text-[10px] sm:text-xs text-[#9CA3AF]">
                              {row.time}
                            </p>
                          </td>
                          <td className="px-2 sm:px-4 py-3 sm:py-4 text-[#111827] truncate max-w-[80px] sm:max-w-none">
                            {row.course}
                          </td>
                          <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex rounded-full px-1 sm:px-2 py-1 text-[10px] sm:text-xs font-medium ${
                                row.type === "Live Classes"
                                  ? "bg-[#02678333] text-[#03A2CE]"
                                  : "bg-[#F3910733] text-[#F39107]"
                              }`}
                            >
                              {row.type}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-3 sm:py-4 hidden sm:table-cell">
                            <div className="flex items-center gap-2">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E5E7EB] text-sm font-semibold text-[#374151] overflow-hidden flex-shrink-0">
                                {row.avatar ? (
                                  <img
                                    src={row.avatar}
                                    alt={row.studentName}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  getInitials(row.studentName)
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-[#111827] truncate">
                                  {row.studentName}
                                </p>
                                <p className="text-xs text-[#6B7280] truncate">
                                  {row.studentEmail}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-3 sm:py-4 font-medium text-[#111827] whitespace-nowrap">
                            {row.amount}
                          </td>
                          <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex capitalize rounded-full px-1 sm:px-2 py-1 text-[10px] sm:text-xs font-medium ${
                                row.status === "approved"
                                  ? "bg-[#22C55E33] text-[#22C55E]"
                                  : "bg-[#FEF3C7] text-[#B45309]"
                              }`}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-3 sm:py-4 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleOpenDetails(row)}
                              className="rounded-full bg-[#C9C9C933] px-2 py-1 text-[10px] sm:text-xs text-[#2B2B2B] whitespace-nowrap"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  : null}

                {!isLoading && !error && pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 sm:px-4 py-6 sm:py-10 text-center text-sm sm:text-base text-[#6B7280]"
                    >
                      No purchases found for the current filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="font-['Roboto'] mt-6 px-3 sm:px-5 flex flex-col sm:flex-row sm:items-center sm:justify-around gap-3">
          <button
            type="button"
            disabled={displayedPage <= 1 || isLoading}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            className="rounded-full border border-[#938A96] px-4 py-1.5 text-xs sm:text-sm text-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>

          <p className="text-xs sm:text-sm text-black order-first sm:order-none">
            Page {displayedPage} of {totalPages}
          </p>

          <button
            type="button"
            disabled={displayedPage >= totalPages || isLoading}
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            className="rounded-full border border-[#923D8C] bg-[#923D8C] px-4 py-1.5 text-xs sm:text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <PaymentDetailsModal
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        purchase={selectedPurchase}
      />
    </section>
  );
};

export default ManagePurchaseView;
